import React, { useState } from 'react';
import { formatKoreanDate } from '../data/fortyTwoProjects';

/**
 * ExamAttemptChart — 42 시험의 회차별 점수 막대그래프
 * x = 응시 회차(왼쪽이 가장 오래된 시험), y = 점수(0~100)
 * 단일 시리즈 강조형: 통과한 회차만 악센트, 나머지는 회색. 통과 막대에는 글자 라벨도 붙여 색에만 의존하지 않는다
 * 색은 dataviz 팔레트 검증기로 카드 표면(라이트 cyan-100 / 다크 ≈ #132839) 기준으로 검증했다 (CLAUDE.md Phase 6 절)
 */
const W = 320;
const H = 150;
const PAD = { left: 28, right: 8, top: 18, bottom: 24 };
const MAX = 100;
const TICKS = [0, 25, 50, 75, 100];

const colorsFor = (theme) =>
  theme === 'dark'
    ? {
        pass: '#0891b2',
        fail: '#6b7280', // gray-500: 악센트보다 어두워 강조가 유지된다. 표면 대비 3.1:1
        grid: 'rgba(255,255,255,0.08)',
        base: 'rgba(255,255,255,0.25)',
        tick: '#9ca3af',
        label: '#e5e7eb',
        tipBg: '#1f2937',
        tipBorder: 'rgba(255,255,255,0.12)',
        tipText: '#f3f4f6',
        tipSub: '#9ca3af',
      }
    : {
        pass: '#0891b2',
        fail: '#9ca3af', // gray-400: 표면 대비(≈2.4:1)를 악센트(≈3.4:1)보다 낮춰 뒤로 물러난다. 값은 툴팁·표로 보완
        grid: 'rgba(0,0,0,0.08)',
        base: 'rgba(0,0,0,0.2)',
        tick: '#6b7280',
        label: '#111827',
        tipBg: '#ffffff',
        tipBorder: 'rgba(0,0,0,0.1)',
        tipText: '#111827',
        tipSub: '#6b7280',
      };

// 위쪽 모서리만 둥근 막대. 기준선 쪽은 각지게
const barPath = (x, y, w, h, r) => {
  const rr = Math.max(0, Math.min(r, w / 2, h));
  return `M${x},${y + h} V${y + rr} A${rr},${rr} 0 0 1 ${x + rr},${y} H${x + w - rr} A${rr},${rr} 0 0 1 ${x + w},${y + rr} V${y + h} Z`;
};

function ExamAttemptChart({ history, currentTheme }) {
  const [hover, setHover] = useState(null);
  const items = Array.isArray(history) ? history : [];
  if (items.length === 0) return null;

  const c = colorsFor(currentTheme);
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const slot = plotW / items.length;
  const barW = Math.min(24, slot * 0.6);
  const baseY = PAD.top + plotH;
  const yOf = (v) => baseY - (Math.max(0, Math.min(MAX, v || 0)) / MAX) * plotH;
  const markOf = (d) => (typeof d.mark === 'number' ? d.mark : 0);
  const attemptOf = (d, i) => d.attempt ?? i + 1;
  const passIndexes = items.map((d, i) => (d.passed ? i : -1)).filter((i) => i >= 0);
  const labeledIndex = passIndexes.length > 0 ? passIndexes[passIndexes.length - 1] : -1;
  const last = items[items.length - 1];
  const summary = `응시별 점수 막대그래프, ${items.length}회 응시, 마지막 ${markOf(last)}점${last.passed ? ' 통과' : ''}`;
  const hovered = hover != null ? items[hover] : null;
  // 툴팁 문구는 span 하나에 문자열 하나로 둔다. 회차·날짜·통과 조각을 따로 두면 막대를 옮길 때 React 가 텍스트 노드를
  // 지우거나 끼우는데, 브라우저 번역이 그 노드를 바꿔 놓았으면 NotFoundError 로 흰 화면이 된다 (CLAUDE.md Phase 8)
  const tipMain = hovered ? `${markOf(hovered)}점` : '';
  const tipSub = hovered
    ? ` · ${attemptOf(hovered, hover)}회차${hovered.date ? ` · ${formatKoreanDate(hovered.date)}` : ''}${hovered.passed ? ' · 통과' : ''}`
    : '';

  return (
    <div className="relative mt-3">
      <p
        className={`text-xs font-semibold mb-1 break-keep ${
          currentTheme === 'dark' ? 'text-gray-400' : 'text-gray-600'
        }`}
      >
        응시별 점수
      </p>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label={summary} style={{ fontFamily: 'inherit' }}>
        {TICKS.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={yOf(t)}
              y2={yOf(t)}
              stroke={t === 0 ? c.base : c.grid}
              strokeWidth="1"
              shapeRendering="crispEdges"
            />
            <text
              x={PAD.left - 6}
              y={yOf(t)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize="9"
              fill={c.tick}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {t}
            </text>
          </g>
        ))}
        {items.map((d, i) => {
          const mark = markOf(d);
          const slotX = PAD.left + slot * i;
          const x = slotX + (slot - barW) / 2;
          const top = yOf(mark);
          const h = mark > 0 ? baseY - top : 2; // 0점 회차는 2px 스텁으로 존재만 표시
          const y = mark > 0 ? top : baseY - 2;
          const dimmed = hover != null && hover !== i;
          return (
            <g
              key={attemptOf(d, i)}
              tabIndex={0}
              aria-label={`${attemptOf(d, i)}회차 ${mark}점${d.passed ? ' 통과' : ''}${d.date ? `, ${formatKoreanDate(d.date)}` : ''}`}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
              style={{ outline: 'none', cursor: 'default' }}
            >
              {/* 히트 영역: 슬롯 전체 (막대보다 넓다) */}
              <rect x={slotX} y={PAD.top} width={slot} height={plotH} fill="transparent" />
              <path
                d={barPath(x, y, barW, h, 4)}
                fill={d.passed ? c.pass : c.fail}
                opacity={dimmed ? 0.6 : 1}
                style={{ transition: 'opacity 120ms' }}
              />
              <text
                x={slotX + slot / 2}
                y={H - PAD.bottom + 12}
                textAnchor="middle"
                fontSize="9"
                fill={c.tick}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {attemptOf(d, i)}
              </text>
              {i === labeledIndex && (
                <text x={x + barW / 2} y={top - 5} textAnchor="middle" fontSize="9" fontWeight="600" fill={c.label}>
                  {mark} · 통과
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {/* key={hover}: 막대가 바뀔 때마다 툴팁 상자를 새로 만든다. 같은 노드의 글자를 제자리에서 바꾸면 브라우저 번역(Google)이 이전 문구로
          보낸 번역 응답이 늦게 도착해 새 값을 덮고 그대로 남는다(이전 막대의 점수·날짜가 굳음, CLAUDE.md Phase 8) */}
      {hovered && (
        <div
          key={hover}
          className="absolute pointer-events-none px-2 py-1 rounded text-xs shadow"
          style={{
            left: `${((PAD.left + slot * hover + slot / 2) / W) * 100}%`,
            top: 0,
            transform: 'translate(-50%, 0)',
            background: c.tipBg,
            border: `1px solid ${c.tipBorder}`,
            color: c.tipText,
            whiteSpace: 'nowrap',
          }}
        >
          <span className="font-semibold">{tipMain}</span>
          <span style={{ color: c.tipSub }}>{tipSub}</span>
        </div>
      )}
      {/* 표 보기 쌍둥이: 화면에는 숨기고 보조 기술에만 */}
      <table className="sr-only">
        <caption>응시별 점수</caption>
        <thead>
          <tr>
            <th>회차</th>
            <th>날짜</th>
            <th>점수</th>
            <th>결과</th>
          </tr>
        </thead>
        <tbody>
          {items.map((d, i) => (
            <tr key={attemptOf(d, i)}>
              <td>{attemptOf(d, i)}</td>
              <td>{d.date || '-'}</td>
              <td>{typeof d.mark === 'number' ? d.mark : '-'}</td>
              <td>{d.passed ? '통과' : '불합격'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ExamAttemptChart;
